/* Header search dropdown — clones the legacy Searchbar popper and fixes
 * what legacy shipped broken: Enter now opens the full filterable results
 * (legacy's Enter was a no-op), Escape/outside-click close the dropdown
 * (legacy only closed via the clear X), arrow keys navigate, and the
 * mobile sheet actually shows its results (legacy rendered them behind
 * the dialog).
 *
 * Data source: the same catalog slice the shop grid caches
 * (?view=shop-data, sessionStorage-shared) — Shopify's predictive-search
 * API rejects Arabic, and local matching is instant in both locales.
 */
(() => {
  const configEl = document.querySelector('[data-ms-search-config]');
  if (!configEl) return;
  let cfg;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch {
    return;
  }

  const conversion = cfg.conversion || {};
  const i18n = cfg.i18n || {};
  const subunitsPerUnit = Math.round(1000 / (cfg.thousandUnits || 1));
  // BHD/KWD/OMR keep the 3-decimal convention even while the store money
  // format serves two — matches money-localized.liquid. Twin in ms-shop.js.
  const storeDecimals = subunitsPerUnit === 1000 || /^(?:BHD|KWD|OMR)$/.test(conversion.currency || 'BHD') ? 3 : 2;
  const DEBOUNCE_MS = 300; // legacy debounceDelay
  const MAX_ROWS = 10; // legacy limit

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const formatMoney = (subunits) => {
    const base = subunits / subunitsPerUnit;
    if (conversion.rate > 0) {
      const converted = (base * conversion.rate).toFixed(conversion.decimals);
      if (conversion.riyalMark) {
        return `<span dir="ltr">${converted}</span><img src="${conversion.riyalMark}" alt="${conversion.currency}" style="display:inline;height:.95em;margin-inline-start:.2em;object-fit:contain;vertical-align:middle;width:auto;">`;
      }
      if (conversion.isArabic) return `<span dir="ltr">${converted}</span>&nbsp;${conversion.label}`;
      return `${converted} ${conversion.currency}`;
    }
    const amount = base.toFixed(storeDecimals);
    if (conversion.isArabic && conversion.label) {
      return `<span dir="ltr">${amount}</span>&nbsp;${conversion.label}`;
    }
    return (cfg.moneyFormat || '{{ amount }}').replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '');
  };

  // ---------- dataset (shared cache with ms-shop.js) ----------
  const cacheKey = `msShopData:${cfg.locale}:all`;
  let products = null;
  let loadPromise = null;
  function ensureData() {
    if (products) return Promise.resolve(products);
    if (loadPromise) return loadPromise;
    loadPromise = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.t < 600000 && Array.isArray(cached.products)) {
          products = cached.products;
          return products;
        }
      } catch { /* ignore */ }
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      const out = [];
      let page = 1;
      let pages = 1;
      do {
        let url = `${cfg.dataUrl}&page=${page}`;
        if (preview) url += `&preview_theme_id=${preview}`;
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`shop-data HTTP ${res.status}`);
        const data = await res.json();
        out.push(...data.products);
        pages = data.pages || 1;
        page += 1;
      } while (page <= pages && page <= 12);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), products: out }));
      } catch { /* fine */ }
      products = out;
      return products;
    })();
    loadPromise.catch(() => { loadPromise = null; });
    return loadPromise;
  }

  // Fold Arabic orthographic variants so hamza-less queries still match
  // (اوسم finds أوسم, مجموعه finds مجموعة). Latin text passes through
  // untouched. Twin of foldArabic in ms-shop.js — keep them identical.
  const foldArabic = (value) => value
    .replace(/[\u0622\u0623\u0625]/g, '\u0627') // آ أ إ -> ا
    .replace(/\u0629/g, '\u0647') // ة -> ه
    .replace(/\u0649/g, '\u064a') // ى -> ي
    .replace(/[\u0640\u064b-\u065f\u0670]/g, ''); // tatweel + diacritics

  // Legacy matched every whitespace term against Arabic/English names + SKU;
  // here: localized title + vendor + handle (the handle carries the English
  // slug, so English terms match even on Arabic pages).
  function searchProducts(query) {
    const terms = foldArabic(query.toLowerCase()).split(/\s+/).filter(Boolean);
    const matched = products.filter((p) => {
      if (!p.available) return false; // legacy inStock=true
      const haystack = foldArabic(`${p.title} ${p.vendor || ''} ${p.handle}`.toLowerCase());
      return terms.every((term) => haystack.includes(term));
    });
    return { total: matched.length, items: matched.slice(0, MAX_ROWS) };
  }

  const resultsHref = (query) => `${cfg.resultsUrl}?q=${encodeURIComponent(query)}`;

  const thumb = (url) => (url && url.includes('width=600') ? url.replace('width=600', 'width=152') : url);

  function rowHtml(p) {
    const onSale = p.compare > p.price;
    const compare = onSale ? `<span class="ms-search-drop__compare">${formatMoney(p.compare)}</span>` : '';
    const img = p.image
      ? `<img src="${escapeHtml(thumb(p.image))}" alt="${escapeHtml(p.imageAlt)}" loading="lazy" width="66" height="66">`
      : '';
    return `<a class="ms-search-drop__row" href="${escapeHtml(p.url)}" data-search-row role="option" aria-selected="false">`
      + `<span class="ms-search-drop__img">${img}</span>`
      + `<span class="ms-search-drop__meta">`
      + `<span class="ms-search-drop__title">${escapeHtml(p.title)}</span>`
      + `<span class="ms-search-drop__price">${compare}${formatMoney(p.price)}</span>`
      + `</span></a>`;
  }

  // ---------- per-instance wiring (desktop wrap + mobile sheet) ----------
  const sheet = document.querySelector('[data-search-sheet]');

  document.querySelectorAll('[data-ms-search]').forEach((scope) => {
    const input = scope.querySelector('[data-search-input]');
    const form = scope.querySelector('[data-search-form]');
    const drop = scope.querySelector('[data-search-drop]');
    const clearBtn = scope.querySelector('[data-search-clear]');
    if (!input || !drop || !form) return;

    let debounceTimer = null;
    let activeIndex = -1;
    let currentQuery = '';
    let wantOpen = false; // cleared on close so late debounce/fetch callbacks can't reopen

    const setExpanded = (open) => input.setAttribute('aria-expanded', String(open));

    function close() {
      wantOpen = false;
      clearTimeout(debounceTimer);
      drop.hidden = true;
      drop.innerHTML = '';
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      setExpanded(false);
    }

    function openLoading() {
      drop.hidden = false;
      drop.innerHTML = '<div class="ms-search-drop__loading"><span class="ms-search-drop__spinner" aria-hidden="true"></span></div>';
      setExpanded(true);
    }

    function renderResults(query) {
      const { total, items } = searchProducts(query);
      if (!items.length) {
        drop.innerHTML = `<h6 class="ms-search-drop__heading">${escapeHtml(i18n.heading)}</h6>`
          + `<p class="ms-search-drop__none">${escapeHtml(i18n.noResultsFor)} "${escapeHtml(query)}"</p>`;
        drop.hidden = false;
        setExpanded(true);
        activeIndex = -1;
        return;
      }
      drop.innerHTML = `<h6 class="ms-search-drop__heading">${escapeHtml(i18n.heading)}</h6>`
        + `<div class="ms-search-drop__list" role="listbox" aria-label="${escapeHtml(i18n.heading)}">${items.map(rowHtml).join('')}</div>`
        + `<a class="ms-search-drop__all" href="${escapeHtml(resultsHref(query))}" data-search-row>${escapeHtml(i18n.viewAll)} (${total})</a>`;
      drop.hidden = false;
      setExpanded(true);
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
    }

    function runSearch() {
      const query = input.value.trim();
      currentQuery = query;
      if (!query) {
        close();
        return;
      }
      if (!products) openLoading();
      ensureData()
        .then(() => {
          if (wantOpen && input.value.trim() === query) renderResults(query);
        })
        .catch(() => {
          // Dataset unreachable — fall back to the server search page.
          close();
        });
    }

    input.addEventListener('input', () => {
      if (clearBtn) clearBtn.hidden = input.value.length === 0;
      clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (!query) {
        close();
        return;
      }
      wantOpen = true;
      // Legacy opens instantly on the first character, then debounces data.
      if (drop.hidden) openLoading();
      debounceTimer = setTimeout(runSearch, DEBOUNCE_MS);
    });

    form.addEventListener('submit', (event) => {
      // Legacy's Enter was dead; ours goes to the full filterable results.
      const query = input.value.trim();
      if (!query) {
        event.preventDefault();
        return;
      }
      if (products || sessionStorage.getItem(cacheKey)) {
        event.preventDefault();
        window.location.assign(resultsHref(query));
      }
      // else: let the native /search?q= submission proceed.
    });

    input.addEventListener('keydown', (event) => {
      const rows = [...drop.querySelectorAll('[data-search-row]')];
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (!rows.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        activeIndex = (activeIndex + delta + rows.length) % rows.length;
        rows.forEach((row, index) => {
          const on = index === activeIndex;
          row.classList.toggle('is-active', on);
          if (row.getAttribute('role') === 'option') row.setAttribute('aria-selected', String(on));
          if (!row.id) row.id = `${input.id || 'ms-search'}-opt-${index}`;
        });
        input.setAttribute('aria-activedescendant', rows[activeIndex].id);
        rows[activeIndex].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        window.location.assign(rows[activeIndex].getAttribute('href'));
      }
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = '';
        clearBtn.hidden = true;
        close();
        input.focus();
      });
    }

    // Capture phase: page widgets that stopPropagation can't block the close.
    document.addEventListener('pointerdown', (event) => {
      if (!scope.contains(event.target)) close();
    }, true);

    scope.msSearchClose = close;
  });

  // ---------- mobile sheet open/close ----------
  const opener = document.querySelector('[data-ms-search-open]');
  if (opener && sheet) {
    opener.addEventListener('click', (event) => {
      event.preventDefault();
      sheet.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      // The floating widgets' CSS keys off this class to get out of the way.
      document.documentElement.classList.add('ms-search-open');
      const input = sheet.querySelector('[data-search-input]');
      if (input) setTimeout(() => input.focus(), 50);
    });
    const closeSheet = () => {
      sheet.hidden = true;
      document.documentElement.style.overflow = '';
      document.documentElement.classList.remove('ms-search-open');
      const panel = sheet.querySelector('[data-ms-search]');
      if (panel && panel.msSearchClose) panel.msSearchClose();
    };
    sheet.querySelectorAll('[data-search-sheet-close]').forEach((el) => {
      el.addEventListener('click', closeSheet);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !sheet.hidden) closeSheet();
    });
    // Enter/row navigation leaves the page with the sheet still open — close
    // it on the way out so a bfcache restore doesn't come back locked.
    window.addEventListener('pagehide', () => {
      if (!sheet.hidden) closeSheet();
    });
  }
})();
